import classNames from 'classnames';
import {Link} from 'react-router';
import {List} from 'immutable';
import React, {useState} from 'react';

import {addGenericErrorToast, LoadingSpinner, Menu, Text} from '@albert-io/atomic';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {getClassroomsQuery} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import useBreakpoint from 'lib/hooks/useBreakpoint';
import useOnResourceStale from './hooks/useOnResourceStale';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

interface Props {
  pathname: string;
}

export default function StudentHomepageSidebarContent({pathname}: Props) {
  const {isSAndDown} = useBreakpoint();
  const currentTab = pathname.split('/').slice(-1)[0];

  const areClassroomsLoading = useStudentDashboard((state) => state.areClassroomsLoading);
  const isReloadingStudent = useStudentDashboard((state) => state.isReloadingStudent);
  const setAreClassroomsLoading = useStudentDashboard((state) => state.setAreClassroomsLoading);
  const setStoreClassrooms = useStudentDashboard((state) => state.setClassrooms);
  const setIsSidebarExpanded = useStudentDashboard((state) => state.setIsSidebarExpanded);
  const setShowJoinClassModal = useStudentDashboard((state) => state.setShowJoinClassModal);
  const studentId = useStudentDashboard((state) => state.student?.getId() ?? '');

  const [classrooms, setClassrooms] = useState(List<ClassroomModelV1>());
  const [classroomsLastFetchedAt, setClassroomsLastFetchedAt] = useState(0);

  useOnResourceStale(classroomsLastFetchedAt, () => {
    const query = getClassroomsQuery(studentId);

    setAreClassroomsLoading(true);
    query.invalidateInterest();
    setClassroomsLastFetchedAt(Date.now());

    query
      .getResourcePromise()
      .then((fetchedClassrooms) => {
        setClassrooms(fetchedClassrooms);
        setStoreClassrooms(fetchedClassrooms);
      })
      .catch((error) => {
        console.error(error);
        addGenericErrorToast();
      })
      .finally(() => {
        setAreClassroomsLoading(false);
      });
  });

  const isLoading = areClassroomsLoading || isReloadingStudent;

  const menuItemClassNames = 'px-x2! py-x1-half! no-underline';

  function onSidebarLinkClick() {
    if (isSAndDown) {
      setIsSidebarExpanded(false);
    }
  }

  return (
    <div className='flex h-full flex-col gap-x3'>
      <div
        className={classNames('flex-1', {
          'flex items-center justify-center': isLoading,
          'max-h-19/20 overflow-y-auto': !isLoading
        })}
      >
        {isLoading ? (
          <LoadingSpinner bottomMarginLevel={0} size={3} />
        ) : (
          <>
            <Text className='mb-x1-half ml-x2 block' color='secondary' size='s'>
              Classrooms
            </Text>
            <Menu.Item
              as={Link}
              className={menuItemClassNames}
              onClick={onSidebarLinkClick}
              preventDefaultClickHandler={false}
              selected={!pathname.includes('class')}
              to={`/home/${currentTab}`}
            >
              All Classes
            </Menu.Item>
            {classrooms.size === 0 ? (
              <div className='my-x2 rounded-x1 border border-dashed border-primary px-x2 py-x3 text-center'>
                <Text color='secondary' size='xs'>
                  Your classes will appear here
                </Text>
              </div>
            ) : (
              <>
                {classrooms.map((classroom) => {
                  const to = `/home/class/${classroom.getId()}/${currentTab}`;

                  return (
                    <Menu.Item
                      className={menuItemClassNames}
                      as={Link}
                      key={classroom.getId()}
                      onClick={onSidebarLinkClick}
                      preventDefaultClickHandler={false}
                      to={to}
                      selected={pathname.includes(to)}
                    >
                      {classroom.getName()}
                    </Menu.Item>
                  );
                })}
                <Menu.Item
                  className='mt-x1-half content-brand!'
                  leftIconProps={{icon: 'plus'}}
                  onClick={() => {
                    setShowJoinClassModal(true);
                  }}
                >
                  Join Class
                </Menu.Item>
              </>
            )}
          </>
        )}
      </div>
      <Menu.Item
        as={Link}
        className={menuItemClassNames}
        leftIconProps={{icon: 'chart-line'}}
        preventDefaultClickHandler={false}
        to='/my-data'
      >
        My Data
      </Menu.Item>
    </div>
  );
}
